import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Users, Download, Clock, Send, Calendar, Tag, Rocket, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminEmailsPage = () => {
  const [earlyAccess, setEarlyAccess] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('early_access');
  const [preview, setPreview] = useState(null);
  const [recent, setRecent] = useState([]);
  const [sendingNow, setSendingNow] = useState(false);

  const loadMarketing = async () => {
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/admin/marketing/weekly-featured/preview`, { withCredentials: true }),
        axios.get(`${API}/admin/marketing/recent`, { withCredentials: true }),
      ]);
      setPreview(p.data);
      setRecent(r.data.sends || []);
    } catch (err) {
      console.warn('Marketing data unavailable:', err?.response?.status);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/emails`, { withCredentials: true });
        setEarlyAccess(data.early_access || []);
        setSubscribers(data.subscribers || []);
        await loadMarketing();
      } catch (err) {
        toast.error('Failed to load email data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const sendFeaturedNow = async () => {
    if (!window.confirm(`Send featured-product email to ${preview?.audience_size ?? '?'} recipients right now?`)) return;
    setSendingNow(true);
    try {
      const { data } = await axios.post(`${API}/admin/marketing/weekly-featured/send-now`, {}, { withCredentials: true });
      if (data.skipped) {
        toast.error(`Skipped: ${data.reason || 'unknown reason'}`);
      } else {
        toast.success(`Sent to ${data.sent} recipients — featured ${data.product}, code ${data.code}`);
        await loadMarketing();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSendingNow(false);
    }
  };

  // Launch email blast state
  const [launchDryRun, setLaunchDryRun] = useState(null);
  const [launchBusy, setLaunchBusy] = useState(false);

  const loadLaunchDryRun = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/launch-email/dry-run`, { withCredentials: true });
      setLaunchDryRun(data);
    } catch (err) {
      console.warn('Launch email dry-run unavailable:', err?.response?.status);
    }
  };

  useEffect(() => { loadLaunchDryRun(); }, []);

  const sendLaunchTestToMe = async () => {
    setLaunchBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/launch-email/preview`, {}, { withCredentials: true });
      toast.success(data.message || 'Test email sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed');
    } finally {
      setLaunchBusy(false);
    }
  };

  const sendLaunchToEveryone = async () => {
    const count = launchDryRun?.would_send ?? '?';
    const confirmed = window.confirm(
      `🚀 LAUNCH EMAIL BLAST\n\nThis will send the "We're Live" email to ${count} unique recipients (early access + subscribers).\n\nThis action cannot be undone. Are you sure?`
    );
    if (!confirmed) return;
    const doubleConfirm = window.prompt(`Type SEND to confirm sending to ${count} people:`);
    if (doubleConfirm !== 'SEND') {
      toast.error('Send cancelled');
      return;
    }
    setLaunchBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/launch-email/send-now`, {}, { withCredentials: true });
      toast.success(`Launch blast complete: ${data.sent} sent, ${data.skipped} skipped, ${data.failed} failed`);
      await loadLaunchDryRun();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Blast failed');
    } finally {
      setLaunchBusy(false);
    }
  };

  const exportCSV = (list, filename) => {
    const csv = 'email,date\n' + list.map(e => `${e.email},${e.date}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${list.length} emails`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activeList = activeTab === 'early_access' ? earlyAccess : subscribers;
  const activeLabel = activeTab === 'early_access' ? 'Early Access' : 'Subscribers';

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="admin-emails-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </Link>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Email Lists
          </h1>
          <Button
            onClick={() => exportCSV(activeList, `renew-labs-${activeTab}`)}
            disabled={activeList.length === 0}
            className="bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 text-[#00F0FF] flex items-center gap-2"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="glass-card p-5" data-testid="early-access-count">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#00F0FF]" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{earlyAccess.length}</p>
            <p className="text-[#A1A7B5] text-sm">Early Access Signups</p>
          </div>
          <div className="glass-card p-5" data-testid="subscribers-count">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{subscribers.length}</p>
            <p className="text-[#A1A7B5] text-sm">Discount Subscribers</p>
          </div>
        </div>

        {/* Launch Email Blast — One-time "We're Live" email */}
        <div className="glass-card p-6 mb-8 border border-[#00F0FF]/30" data-testid="launch-email-section">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-[#00F0FF]" />
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Launch Email Blast
              </h2>
            </div>
            <span className="text-xs text-[#A1A7B5]">Early Access + Popup Subscribers</span>
          </div>

          <div className="bg-[#111621] rounded-lg p-5 mb-4 border border-[#1E2532]">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs text-[#A1A7B5] uppercase tracking-wide mb-1">Recipients ready</p>
                <p className="text-3xl font-bold text-[#00F0FF]" data-testid="launch-recipient-count">
                  {launchDryRun ? launchDryRun.would_send : '—'}
                </p>
                <p className="text-[#A1A7B5] text-xs mt-1">unique emails across early access + subscribers</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={sendLaunchTestToMe}
                  disabled={launchBusy}
                  className="px-4 py-2 rounded-md bg-[#1E2532] hover:bg-[#2A3344] text-white text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="launch-test-btn"
                >
                  Send Test to Me
                </button>
                <button
                  onClick={sendLaunchToEveryone}
                  disabled={launchBusy || !launchDryRun?.would_send}
                  className="px-5 py-2 rounded-md bg-[#00F0FF] hover:bg-[#00D0DD] text-black text-sm font-bold transition-colors disabled:opacity-40 flex items-center gap-2"
                  data-testid="launch-blast-btn"
                >
                  <Rocket className="w-4 h-4" />
                  {launchBusy ? 'Sending…' : 'Send to Everyone'}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2 text-xs text-[#A1A7B5] bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
            <div>
              <strong className="text-yellow-400">One-time send.</strong> Already-emailed addresses are auto-skipped within 7 days. Send a test to yourself first to verify the email looks right before blasting everyone.
            </div>
          </div>
        </div>

        {/* Marketing Campaigns */}
        <div className="glass-card p-6 mb-8" data-testid="marketing-section">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#00F0FF]" />
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Marketing Campaigns
              </h2>
            </div>
            <span className="text-xs text-[#A1A7B5]">Auto-sends Tuesday 10 AM CT &middot; Holidays at 9 AM CT</span>
          </div>

          {/* Next weekly featured preview */}
          {preview?.product ? (
            <div className="bg-[#111621] rounded-lg p-5 mb-4 border border-[#1E2532]" data-testid="marketing-preview">
              <p className="text-xs text-[#A1A7B5] uppercase tracking-wide mb-3">Next Tuesday's Featured Pick</p>
              <div className="flex items-center gap-4">
                {preview.product.image_url && (
                  <img src={preview.product.image_url} alt={preview.product.name} className="w-16 h-16 rounded-lg object-cover bg-[#0C0E14]" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{preview.product.name}</p>
                  <p className="text-[#A1A7B5] text-sm">
                    ${Number(preview.product.price).toFixed(2)} &middot; sending to <span className="text-[#00F0FF]">{preview.audience_size}</span> {preview.audience_size === 1 ? 'person' : 'people'} &middot; 10% off code expires in 7 days
                  </p>
                  {preview.product.last_featured_at ? (
                    <p className="text-[#A1A7B5] text-xs mt-1">Last featured {new Date(preview.product.last_featured_at).toLocaleDateString()}</p>
                  ) : (
                    <p className="text-green-400 text-xs mt-1">Never featured before</p>
                  )}
                </div>
                <Button
                  onClick={sendFeaturedNow}
                  disabled={sendingNow || preview.audience_size === 0}
                  className="btn-primary"
                  data-testid="marketing-send-now"
                >
                  {sendingNow ? 'Sending…' : 'Send Now'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-[#111621] rounded-lg p-5 mb-4 text-center text-[#A1A7B5] text-sm">
              No eligible product to feature (catalog empty or all out of stock).
            </div>
          )}

          {/* Recent sends */}
          <div data-testid="marketing-history">
            <p className="text-xs text-[#A1A7B5] uppercase tracking-wide mb-2 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" /> Recent Sends
            </p>
            {recent.length === 0 ? (
              <p className="text-[#A1A7B5] text-sm py-3">No campaign sends yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#A1A7B5] text-xs uppercase tracking-wide border-b border-[#1E2532]">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Type</th>
                      <th className="py-2 pr-3">Subject</th>
                      <th className="py-2 pr-3">Code</th>
                      <th className="py-2 pr-3 text-right">Sent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1E2532]/60">
                    {recent.map((s) => (
                      <tr key={s.id} className="text-white" data-testid={`marketing-row-${s.id}`}>
                        <td className="py-2.5 pr-3 text-[#A1A7B5]">{s.sent_at ? new Date(s.sent_at).toLocaleString() : '—'}</td>
                        <td className="py-2.5 pr-3">
                          <span className={`inline-block px-2 py-0.5 text-[10px] rounded uppercase tracking-wide ${s.campaign === 'weekly_featured' ? 'bg-[#00F0FF]/10 text-[#00F0FF]' : 'bg-purple-500/10 text-purple-400'}`}>
                            {s.campaign === 'weekly_featured' ? 'Featured' : 'Holiday'}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 truncate max-w-[200px]">{s.product_name || s.holiday || '—'}</td>
                        <td className="py-2.5 pr-3 font-mono text-xs">
                          {s.promo_code ? (
                            <span className="inline-flex items-center gap-1 text-[#00F0FF]"><Tag className="w-3 h-3" />{s.promo_code}</span>
                          ) : '—'}
                        </td>
                        <td className="py-2.5 pr-3 text-right">{s.sent_count} / {s.audience_size}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex mb-6 bg-[#0C0E14] rounded-lg p-1 border border-[#1E2532]">
          <button
            onClick={() => setActiveTab('early_access')}
            className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'early_access' ? 'bg-[#00F0FF] text-black' : 'text-[#A1A7B5] hover:text-white'}`}
            data-testid="tab-early-access"
          >
            Early Access ({earlyAccess.length})
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'subscribers' ? 'bg-[#00F0FF] text-black' : 'text-[#A1A7B5] hover:text-white'}`}
            data-testid="tab-subscribers"
          >
            Subscribers ({subscribers.length})
          </button>
        </div>

        {/* Email List */}
        <div className="glass-card overflow-hidden">
          {activeList.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-10 h-10 text-[#A1A7B5] mx-auto mb-3" />
              <p className="text-[#A1A7B5]">No {activeLabel.toLowerCase()} emails yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#A1A7B5] border-b border-[#1E2532]">
                    <th className="p-4 w-8">#</th>
                    <th className="p-4">Email</th>
                    <th className="p-4">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E2532]">
                  {activeList.map((item, i) => (
                    <tr key={i} className="text-white hover:bg-[#111621] transition-colors" data-testid={`email-row-${i}`}>
                      <td className="p-4 text-[#A1A7B5]">{i + 1}</td>
                      <td className="p-4 font-medium">{item.email}</td>
                      <td className="p-4 text-[#A1A7B5]">{item.date ? new Date(item.date).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmailsPage;
